import type { ModerationMetricsDto } from '@planuze/admin-contracts';
import { Skeleton } from '@planuze/ui';
import { CheckCircle2, Clock, Gauge, type LucideIcon, XCircle, Zap } from 'lucide-react';
import type { PackSubmissionsContent } from '../../content/sections/pack-submissions.js';
import { usePackSubmissionsContent } from '../../content/sections/pack-submissions.js';
import { MetricCard } from '../metrics/metric-card.js';

interface CardSpec {
  readonly label: string;
  readonly value: string;
  readonly icon: LucideIcon;
  readonly hint?: string;
  readonly highlight?: boolean;
}

const CARD_COUNT = 6;

/* Deriva os cards de SLA da moderação declarativamente (§4.18). O card de SLA destaca
   (highlight destrutivo) quando há decisões/pendências acima do alvo. */
function cardsFor(
  m: ModerationMetricsDto,
  copy: PackSubmissionsContent['metrics'],
): readonly CardSpec[] {
  return [
    { label: copy.queueDepthLabel, value: String(m.queueDepth), icon: Clock },
    {
      label: copy.aboveSlaLabel(m.slaTargetHours),
      value: String(m.aboveSlaCount),
      icon: Gauge,
      highlight: m.aboveSlaCount > 0,
    },
    {
      label: copy.decisionP50P95Label,
      value: `${m.timeToDecisionP50Hours}h / ${m.timeToDecisionP95Hours}h`,
      icon: Clock,
    },
    {
      label: copy.approvedRejectedLabel,
      value: `${m.approvedCount} / ${m.rejectedCount}`,
      icon: CheckCircle2,
    },
    { label: copy.fastpathLabel, value: String(m.fastpathCount), icon: Zap },
    { label: copy.autoRejectCapLabel, value: String(m.autoRejectByCapCount), icon: XCircle },
  ];
}

export function ModerationMetricsGrid({
  metrics,
}: {
  readonly metrics: ModerationMetricsDto | undefined;
}) {
  const copy = usePackSubmissionsContent();
  if (metrics === undefined) {
    return (
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
        {Array.from({ length: CARD_COUNT }, (_, i) => (
          <Skeleton key={i} className="h-24" />
        ))}
      </div>
    );
  }
  return (
    <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
      {cardsFor(metrics, copy.metrics).map((card) => (
        <MetricCard key={card.label} {...card} />
      ))}
    </div>
  );
}
